/**
 * useBandRecords — paginated records for ONE open conduct band ("view more").
 *
 * GET /v1/datasets/{id}/outcomes-report/outcomes/{outcomeId}/bands/{band}/records
 *     ?framework=cd|tcf&limit=&offset=&<filters>
 *
 * Backs the band tile's "View more": the tile shows a cheap top-N sample from
 * the detail payload; once a band is open this pages the full list, offset by
 * offset, appending. `useInfiniteQuery` because the same accumulate-and-append
 * shape also drives a modal if the UI ever grows one.
 */

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { FilterConfig } from '../../../records/filter';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams } from '../../cause-report/utils';
import { BAND_RECORDS_PAGE_SIZE, MONITORING_STALE_TIME, monitoringKeys } from '../constants';
import type { Band, Framework, HighBandRecord } from '../types';

interface HighBandRecordApi {
  id: string;
  score: number;
  headline: string;
  channel: string | null;
  occurred_at: string | null;
  outcome_id: string;
  outcome_name: string;
}

interface BandRecordsApiResponse {
  records: HighBandRecordApi[];
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
}

const mapRecord = (r: HighBandRecordApi): HighBandRecord => ({
  id: r.id,
  score: r.score,
  headline: r.headline,
  channel: r.channel,
  occurredAt: r.occurred_at,
  outcomeId: r.outcome_id,
  outcomeName: r.outcome_name,
});

async function fetchBandRecords(
  datasetId: string,
  framework: Framework,
  outcomeId: string,
  band: Band,
  params: Record<string, string>,
  offset: number,
  bucketStart: string | null,
  bucketEnd: string | null,
): Promise<BandRecordsApiResponse> {
  const qs = new URLSearchParams({
    framework,
    ...params,
    limit: String(BAND_RECORDS_PAGE_SIZE),
    offset: String(offset),
  });
  // Scope to a trend-chart bucket when one is selected, so paged rows match the
  // bucket-scoped tile count (the detail view scopes the sample the same way).
  if (bucketStart) qs.set('bucket_start', bucketStart);
  if (bucketEnd) qs.set('bucket_end', bucketEnd);
  return fetchWithAuth(
    `/v1/datasets/${datasetId}/outcomes-report/outcomes/${outcomeId}/bands/${band}/records?${qs.toString()}`,
  );
}

export interface UseBandRecordsResult {
  records: HighBandRecord[];
  total: number;
  hasMore: boolean;
  loadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
}

/**
 * @param enabled  false until a band is actually open, so nothing is fetched
 *                 while the tiles just sit there.
 */
export function useBandRecords(
  datasetId: string,
  framework: Framework,
  outcomeId: string | null,
  band: Band | null,
  filters: FilterConfig,
  enabled: boolean,
  bucketStart: string | null = null,
  bucketEnd: string | null = null,
): UseBandRecordsResult {
  const params = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useInfiniteQuery({
    // outcomeId + band + params + bucket are all in the key, so changing the open
    // band, the report filters, or the selected chart bucket starts a fresh
    // query — no stale pages carry over.
    queryKey: [
      ...monitoringKeys.bandRecords(datasetId, framework, outcomeId ?? '', band ?? 'low', params),
      bucketStart ?? '',
      bucketEnd ?? '',
    ],
    queryFn: ({ pageParam }) =>
      fetchBandRecords(datasetId, framework, outcomeId!, band!, params, pageParam, bucketStart, bucketEnd),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.has_more ? last.offset + last.limit : undefined),
    staleTime: MONITORING_STALE_TIME,
    enabled: enabled && !!datasetId && !!outcomeId && !!band,
  });

  const records = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.records.map(mapRecord)),
    [query.data],
  );

  return {
    records,
    total: query.data?.pages[0]?.total ?? 0,
    hasMore: !!query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    isError: query.isError,
  };
}
